/**
 * Record something that is not on the calendar.
 *
 * Two ways in, behind one control: send the recorder to a meeting happening
 * now, or upload one that already happened. They are the same question — "put
 * this on the record" — asked before and after the fact, so they share a button
 * rather than competing for space in the bar.
 *
 * In the top bar beside settings rather than on the Meetings page, because both
 * produce a meeting from *outside* the list. A control that adds to the list
 * sitting inside the list reads as filtering it.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Plus, Upload, Video } from 'lucide-react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from '@/components/ui/overlays'
import { Field, Input, Label } from '@/components/ui/field'
import {
  looksLikeMeetingUrl,
  sendBot,
  uploadRecording,
  type UploadPhase,
} from '@/data/capture'
import { cn } from '@/lib/utils'

export function CaptureMenu() {
  const [open, setOpen] = useState<'bot' | 'upload' | null>(null)

  return (
    <>
      <Menu>
        <MenuTrigger
          aria-label="Record a meeting"
          className={cn(
            'grid size-8 place-items-center rounded-[var(--radius-sm)] outline-none transition-colors',
            'text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)]',
            'focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/35'
          )}
        >
          <Plus className="size-4" />
        </MenuTrigger>
        <MenuContent align="end" className="w-64">
          <MenuItem onClick={() => setOpen('bot')}>
            <Video className="size-3.5" />
            <span className="flex-1">Send the recorder to a meeting</span>
          </MenuItem>
          <MenuItem onClick={() => setOpen('upload')}>
            <Upload className="size-3.5" />
            <span className="flex-1">Upload a recording</span>
          </MenuItem>
        </MenuContent>
      </Menu>

      <SendBotDialog open={open === 'bot'} onClose={() => setOpen(null)} />
      <UploadDialog open={open === 'upload'} onClose={() => setOpen(null)} />
    </>
  )
}

/**
 * Sending the recorder somewhere.
 *
 * A link is the whole input. The title is optional because the recorder reads
 * the meeting's own name off the client once it is in the room, and typing one
 * here would only override something more accurate.
 */
function SendBotDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [url, setUrl] = useState('')
  const queryClient = useQueryClient()

  const send = useMutation({
    mutationFn: () => sendBot({ url }),
    onSuccess: (result) => {
      // Which recorder went matters: the fallback is a third party joining the
      // call, and someone about to be on that call should know.
      toast.success(
        result.fellBack
          ? 'The recorder is on its way — using the fallback provider.'
          : 'The recorder is on its way.'
      )
      void queryClient.invalidateQueries({ queryKey: ['meetings'] })
      setUrl('')
      onClose()
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const valid = looksLikeMeetingUrl(url)

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent size="sm">
        <form
          onSubmit={(event) => {
            event.preventDefault()
            if (valid) send.mutate()
          }}
        >
          <DialogHeader
            title="Send the recorder"
            description="It joins within about a minute and records from the moment it arrives."
          />
          <DialogBody className="space-y-3">
            {/* The header already says what happens; the hint names the
                platforms, which is the only thing left to say. */}
            <Field label="Meeting link" hint="Teams, Zoom or Meet.">
              {(props) => (
                <Input
                  {...props}
                  autoFocus
                  value={url}
                  placeholder="https://teams.microsoft.com/l/meetup-join/…"
                  onChange={(event) => setUrl(event.target.value)}
                />
              )}
            </Field>

            {url.trim() && !valid ? (
              <p className="text-2xs text-[var(--color-danger)]">
                That does not look like a meeting link.
              </p>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || send.isPending}>
              {send.isPending ? <Loader2 className="size-3.5 animate-spin" /> : null}
              Send the recorder
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const PHASE_LABEL: Record<UploadPhase, string> = {
  preparing: 'Preparing…',
  uploading: 'Uploading',
  processing: 'Processing — you can close this',
}

/**
 * Uploading a recording that happened elsewhere.
 *
 * The file goes straight to storage, so the dialog reports its own progress
 * rather than a spinner: an hour of video is minutes of upload, and a control
 * that looks identical at 5% and 95% invites someone to close the tab.
 */
function UploadDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null)
  const [name, setName] = useState('')
  const [phase, setPhase] = useState<UploadPhase | null>(null)
  const [percent, setPercent] = useState<number | null>(null)
  const input = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const reset = () => {
    setFile(null)
    setName('')
    setPhase(null)
    setPercent(null)
  }

  const upload = useMutation({
    mutationFn: () =>
      uploadRecording(
        { file: file!, name: name.trim() || file!.name },
        { onPhase: setPhase, onProgress: setPercent }
      ),
    onSuccess: () => {
      toast.success('Uploaded. The notes will be written shortly.')
      void queryClient.invalidateQueries({ queryKey: ['meetings'] })
      reset()
      onClose()
    },
    onError: (error: Error) => {
      toast.error(error.message)
      setPhase(null)
    },
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closing mid-upload would abandon a transfer with no way back to it.
        if (!next && !upload.isPending) {
          reset()
          onClose()
        }
      }}
    >
      <DialogContent size="sm">
        <DialogHeader
          title="Upload a recording"
          description="Audio or video from a meeting that happened somewhere else."
        />
        <DialogBody className="space-y-3">
          <button
            type="button"
            onClick={() => input.current?.click()}
            disabled={upload.isPending}
            className={cn(
              'flex w-full items-center gap-2.5 rounded-[var(--radius-sm)] border border-dashed px-3 py-4 text-left transition-colors',
              'border-[var(--color-border)] hover:border-[var(--color-border-strong)] hover:bg-[var(--color-surface-hover)]',
              'disabled:opacity-60'
            )}
          >
            <Upload className="size-4 shrink-0 text-[var(--color-fg-subtle)]" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs text-[var(--color-fg)]">
                {file ? file.name : 'Choose an audio or video file'}
              </span>
              <span className="block text-2xs text-[var(--color-fg-subtle)]">
                {file ? `${(file.size / 1_000_000).toFixed(0)} MB` : 'mp4, m4a, mp3, wav'}
              </span>
            </span>
          </button>
          <input
            ref={input}
            type="file"
            accept="audio/*,video/*"
            className="hidden"
            onChange={(event) => {
              const chosen = event.target.files?.[0] ?? null
              setFile(chosen)
              // The filename without its extension is a better first guess at a
              // meeting name than an empty field.
              if (chosen && !name) setName(chosen.name.replace(/\.[^.]+$/, ''))
            }}
          />

          <Field label="Meeting name">
            {(props) => (
              <Input
                {...props}
                value={name}
                disabled={upload.isPending}
                placeholder="What this meeting was"
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>

          {phase ? (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>{PHASE_LABEL[phase]}</Label>
                {phase === 'uploading' && percent !== null ? (
                  <span className="text-2xs tabular-nums text-[var(--color-fg-subtle)]">
                    {percent}%
                  </span>
                ) : null}
              </div>
              <div className="h-1 overflow-hidden rounded-full bg-[var(--color-surface-sunken)]">
                <div
                  className="h-full bg-[var(--color-primary)] transition-[width]"
                  style={{ width: `${phase === 'uploading' ? (percent ?? 0) : 100}%` }}
                />
              </div>
            </div>
          ) : null}

        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={upload.isPending}>
            Cancel
          </Button>
          <Button onClick={() => upload.mutate()} disabled={!file || upload.isPending}>
            {upload.isPending ? <Loader2 className="size-3.5 animate-spin" /> : null}
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
